import React from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Search, Calculator, Send, Power, ArrowRight } from "lucide-react";
import Layout from "../components/Layout";
import RevealText from "../components/RevealText";

const STEPS = [
  { icon: Search, key: "step1" },
  { icon: Calculator, key: "step2" },
  { icon: Send, key: "step3" },
  { icon: Power, key: "step4" },
];

export default function HowItWorks() {
  const { t } = useTranslation();

  return (
    <Layout>
      <Helmet>
        <title>{t("howPage.metaTitle")} — Velcor Energy</title>
        <meta name="description" content={t("howPage.metaDesc")} />
      </Helmet>

      <section className="relative bg-velcor-cream pt-12 md:pt-20 pb-16 md:pb-20" data-testid="how-hero">
        <div className="max-w-[980px] mx-auto px-5 text-center">
          <span className="inline-block text-velcor-green mb-3" style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.22em", textTransform: "uppercase" }}>
            {t("howPage.kicker")}
          </span>
          <h1 className="display text-velcor-ink leading-[1.05]" style={{ fontSize: "clamp(32px, 5vw, 56px)" }}>
            <RevealText text={t("howPage.heroPre")} />{" "}
            <RevealText text={t("howPage.heroAccent")} className="display-accent" delay={0.2} />
          </h1>
          <p className="mt-5 text-slate-600 max-w-[640px] mx-auto leading-relaxed" style={{ fontSize: 16 }}>
            {t("howPage.heroLead")}
          </p>
        </div>
      </section>

      {/* 4 detailed steps */}
      <section className="bg-white py-20" data-testid="how-steps">
        <div className="max-w-[980px] mx-auto px-5 space-y-16 md:space-y-20">
          {STEPS.map((s, i) => (
            <motion.div
              key={s.key}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
              className="grid md:grid-cols-[120px_1fr] gap-6 md:gap-10 items-start"
              data-testid={`how-step-${i + 1}`}
            >
              <div className="flex md:flex-col items-center md:items-start gap-3">
                <span className="grid place-items-center w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-velcor-green/10 text-velcor-green shrink-0 ring-1 ring-velcor-green/20">
                  <s.icon className="w-7 h-7 md:w-8 md:h-8" strokeWidth={1.8} />
                </span>
              </div>
              <div>
                <h2 className="display text-velcor-ink leading-[1.1]" style={{ fontSize: "clamp(24px, 2.8vw, 32px)" }}>
                  {t(`howPage.${s.key}Title`)}
                </h2>
                <p className="mt-3 text-slate-600 leading-relaxed" style={{ fontSize: 15 }}>
                  {t(`howPage.${s.key}Body`)}
                </p>
                <ul className="mt-4 space-y-2 text-slate-500 text-[14px]">
                  {[1, 2, 3].map((n) => (
                    <li key={n} className="flex items-start gap-2.5">
                      <span className="block w-1.5 h-1.5 rounded-full bg-velcor-green mt-2 shrink-0" />
                      {t(`howPage.${s.key}Bullet${n}`)}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-velcor-cream py-20 text-center">
        <div className="max-w-[680px] mx-auto px-5">
          <h2 className="display text-velcor-ink leading-[1.1]" style={{ fontSize: "clamp(26px, 3vw, 38px)" }}>
            {t("howPage.ctaTitle")}{" "}
            <span className="display-accent">{t("howPage.ctaTitleAccent")}</span>
          </h2>
          <div className="mt-7 flex justify-center">
            <Link to="/?focus=address#calcular" className="btn-green inline-flex items-center h-12 px-6" data-testid="how-cta-primary">
              {t("howPage.ctaPrimary")}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
